// hooks/use-dynamic-svg.ts
import { useCallback, useEffect, useMemo, useState } from 'react'

interface Tone { id: string, hex: string, area: string }

interface UseDynamicSvgParams {
  /** URL de la plantilla SVG original (sin colores) */
  src: string
  /** Tonos iniciales a aplicar en cuanto el SVG se cargue */
  initialTones: Tone[]
}

/**
 * Hook that loads an external SVG, lets you mutate its colors
 * and exposes both the inline string and a data-uri version.
 */
export function useDynamicSvg({ src, initialTones }: UseDynamicSvgParams) {
  const [svgText, setSvgText] = useState<string | null>(null)

  /** Loads the raw SVG once */
  useEffect(() => {
    if (!src.endsWith('.svg')) {
      return setSvgText(null)
    };
    (async () => {
      try {
        const text = await fetch(src).then(r => r.text())
        setSvgText(applyTones(text, initialTones))
      }
      catch (err) {
        console.error('Failed loading SVG: ', err)
      }
    })()
  }, [src, initialTones])

  /** Applies or replaces a tone for a given area */
  const updateTone = useCallback((area: string, hex: string) => {
    setSvgText(current =>
      current ? applyTones(current, [{ area, hex, id: area }]) : current,
    )
  }, [])

  /** Derive data-uri; recalculated solo si cambia svgText */
  const dataUri = useMemo(() => {
    if (!svgText)
      return null
    return `data:image/svg+xml,${encodeURIComponent(svgText)}`
  }, [svgText])
  return { svgText, dataUri, updateTone }
}

/* ---------- helpers ---------- */

/**
 * Verifica si un elemento tiene una clase CSS con fill:none
 * @param element - Elemento SVG a verificar
 * @param doc - Documento SVG para acceder a los estilos
 * @returns true si el elemento tiene fill:none, false en caso contrario
 */
function hasClassWithFillNone(element: SVGElement, doc: Document): boolean {
  const className = element.getAttribute('class')
  if (!className)
    return false

  // Buscar elementos <style> en el SVG
  const styleElements = doc.querySelectorAll('style')

  for (const styleElement of styleElements) {
    const cssText = styleElement.textContent || ''

    // Buscar la clase específica en el CSS con diferentes formatos posibles:
    // .cls-1{fill:none;} o .cls-1 { fill: none; } etc.
    const patterns = [
      // Patrón principal: .className{...fill:none...}
      new RegExp(`\\.${className}\\s*\\{[^}]*fill\\s*:\\s*none[^}]*\\}`, 'i'),
      // Patrón con múltiples clases: .cls-1,.cls-2{...fill:none...}
      new RegExp(
        `\\.${className}\\s*[,\\s][^{]*\\{[^}]*fill\\s*:\\s*none[^}]*\\}`,
        'i',
      ),
      // Patrón inverso: buscar fill:none dentro de una regla que incluye la clase
      new RegExp(
        `\\.[^{]*${className}[^{]*\\{[^}]*fill\\s*:\\s*none[^}]*\\}`,
        'i',
      ),
    ]

    for (const pattern of patterns) {
      if (pattern.test(cssText)) {
        return true
      }
    }
  }

  return false
}

function applyTones(svg: string, tones: Tone[]) {
  const doc = new DOMParser().parseFromString(svg, 'image/svg+xml')
  tones.forEach(({ area, hex, id }) => {
    doc.querySelectorAll<SVGElement>(`[data-area="${area}"]`).forEach((el) => {
      // Ignorar elementos con height > 500 (elementos de diseñador que no deben colorearse)
      const height = el.getAttribute('height')
      if (height && parseFloat(height) > 500) {
        return // Skip este elemento
      }

      // Ignorar elementos que tienen una clase CSS con fill:none
      if (hasClassWithFillNone(el, doc)) {
        return // Skip este elemento
      }

      el.setAttribute('data-tone-id', id)
      el.style.fill = hex
    })
  })
  return new XMLSerializer().serializeToString(doc)
}
